<template>
  <v-row no-gutters>
    <v-col v-if="isFetched">
      <v-row no-gutters v-if="computedHistory.length === 0">
        <v-col class="mb-10 no-content">
          {{ $i18n.t('admin.noData') }}
        </v-col>
      </v-row>

      <sidebar-row
        v-for="(item, index) in computedHistory"
        :key="index"
        align-class="align-center"
        row-class="fill-height"
      >
        <template #actionIcon>
          <v-row no-gutters class="justify-center fill-height align-center" :class="getLadderClass(index)">
            <v-col cols="auto">
              <div class="outSideIcon">
                <v-icon color="#fff" small> {{ item.icon }} </v-icon>
              </div>
            </v-col>
          </v-row>
        </template>
        <template #default>
          <v-row no-gutters class="historyContainer my-2">
            <v-col cols="auto">
              <div class="font-italic font-weight-light pa-2 text-left" style="font-size: 8px">
                {{ item.date }}
              </div>
              <div class="historyText" style="font-size: 11px">
                {{ item.text }}
              </div>

              <template v-if="item.changes">
                <div v-for="(change, index) in item.changes" :key="index" class="changesTextRow">
                  <template v-if="change.updated_objects">
                    <span class="attributeValue">{{ `${change.updated_objects.attribute}:  ` }}</span>
                    <div
                      v-for="(innerChange, index) in change.updated_objects.changes"
                      :key="index"
                      class="changesTextRow"
                    >
                      <span class="attributeValue">{{ `${innerChange.attribute}:  ` }}</span>
                      <span class="oldValue">{{ innerChange.old_value }}</span>
                      <span>{{ '  &#8594;  ' }}</span>
                      <span class="newValue">{{ innerChange.new_value }}</span>
                    </div>
                  </template>
                  <template v-if="change.inserted_objects">
                    <span class="newValue">
                      {{ `+ ${change.inserted_objects.attribute}: (${change.inserted_objects.id})` }}
                    </span>
                  </template>
                  <template v-if="change.deleted_objects">
                    <span class="oldValue">
                      {{ `+ ${change.deleted_objects.attribute}: (${change.deleted_objects.id})` }}
                    </span>
                  </template>
                  <template v-if="change.attribute === 'geom'">
                    <span class="newValue">{{ `&#8594;  ${$i18n.t('sidebar.geometryChanged')}` }}</span>
                  </template>
                  <template v-if="change.attribute && change.attribute !== 'geom'">
                    <span class="attributeValue">{{ `${change.attribute}:  ` }}</span>
                    <span class="oldValue">{{ change.old_value }}</span>
                    <span>{{ '  &#8594;  ' }}</span>
                    <span class="newValue">{{ change.new_value }}</span>
                  </template>
                </div>
              </template>
            </v-col>
          </v-row>
        </template>
      </sidebar-row>
    </v-col>
    <v-col v-else>
      <main-loader />
    </v-col>
  </v-row>
</template>

<script>
import { call, get } from 'vuex-pathify';
import MainLoader from '@/components/MainLoader';
import SidebarRow from '@/components/SidebarRow';

export default {
  name: 'ChangeHistory',
  components: { SidebarRow, MainLoader },
  props: {
    featureId: {
      type: [String, Number],
      required: true,
    },
    dataSourceName: {
      type: String,
      required: true,
    },
  },
  data: () => ({
    isFetched: false,
  }),
  computed: {
    history: get('changeHistory/history'),
    computedHistory() {
      return this.history.map(item => {
        return {
          ...item,
          icon: this.getItemIcon(item.change_type),
          date: this.dateText(item.timestamp),
          text: this.eventText(item),
          ...(item.changes.length > 0 && {
            changes: item.changes.map(change => {
              return {
                ...change,
                old_value: this.getValue(change.old_value),
                new_value: this.getValue(change.new_value),
              };
            }),
          }),
        };
      });
    },
  },
  methods: {
    getHistory: call('changeHistory/getHistory'),
    getValue(change) {
      return change == 0 || change === null ? 'brak danych' : change;
    },
    getLadderClass(index) {
      if (index === 0) {
        return 'ladderFirst';
      } else if (index === this.computedHistory.length - 1) {
        return 'ladderLast';
      } else {
        return 'ladder';
      }
    },
    async init() {
      await this.getHistory({
        data_source_name: this.dataSourceName,
        feature_id: this.featureId,
      });
      this.isFetched = true;
    },
    eventText(item) {
      const { user_name, change_type } = item;
      return this.$i18n.t(`sidebar.object_${change_type}`, { user_name });
    },
    dateText(date) {
      if (date === undefined) {
        return this.$i18n.t('admin.noData');
      } else {
        return this.$dayjs(date).format('L, LTS');
      }
    },
    getItemIcon(value) {
      if (value === 'inserted') {
        return 'mdi-plus';
      } else if (value === 'updated') {
        return 'mdi-pencil';
      } else return 'mdi-delete';
    },
  },
  watch: {
    featureId() {
      this.init();
    },
  },
  mounted() {
    this.init();
    this.$root.$off('getObjectHistory');
    this.$root.$on('getObjectHistory', payload => {
      this.getHistory(payload);
      this.isFetched = true;
    });
  },
};
</script>

<style scoped lang="scss">
.historyContainer {
  border: 1px solid rgba(0, 0, 0, 0.12);
}
.outSideIcon {
  background: #1a73e8;
  box-shadow: 0 1px 5px 0 #0000001f, 0 2px 2px 0 #00000024, 0 1px 1px 0 #00000033;

  display: flex;
  align-items: center;
  justify-content: center;
  border: 4px solid white;
  position: relative;
  z-index: 1;
  border-radius: 50%;
  height: 34px;
  width: 34px;
}
.ladder,
.ladderFirst,
.ladderLast {
  position: relative;
}
.ladder::before {
  content: '';
  height: 100%;
  position: absolute;
  z-index: 0;
  top: 0;
  left: 50%;
  border-left: 2px solid rgba(0, 0, 0, 0.12);
}
.ladderFirst::before {
  content: '';
  height: 50%;
  position: absolute;
  z-index: 0;
  top: 50%;
  left: 50%;
  border-left: 2px solid rgba(0, 0, 0, 0.12);
}
.ladderLast::before {
  content: '';
  height: 50%;
  position: absolute;
  z-index: 0;
  top: 0;
  left: 50%;
  border-left: 2px solid rgba(0, 0, 0, 0.12);
}
.historyText {
  padding: 0 40px 4px 7px;
  text-align: left;
}
.changesTextRow {
  padding: 4px 40px 4px 7px;
  text-align: left;
  font-family: Roboto;
  font-style: normal;
  font-weight: normal;
  font-size: 11px;
  line-height: 13px;
}
.no-content {
  font-size: 18px;
  line-height: 16px;
  color: #aaaaaa;
  padding: 1.5rem;
}
.attributeValue {
  color: #00000096;
}
.oldValue {
  color: #bf360c;
}
.newValue {
  color: #188038;
}
</style>
